import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/lib/auth-store";
import type { AdminPermission } from "@/types/auth";

interface ProtectedProps {
  children: JSX.Element;
  requireAdmin?: boolean;
  requireSuperadmin?: boolean;
  permission?: AdminPermission;
}

export function ProtectedRoute({ children, requireAdmin, requireSuperadmin, permission }: ProtectedProps) {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const location = useLocation();

  if (status === "loading" || status === "idle") {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (status !== "authenticated" || !user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?next=${next}`} replace />;
  }

  if (requireSuperadmin && !user.is_superadmin) {
    return <Forbidden />;
  }
  if (requireAdmin && !user.is_admin) {
    return <Forbidden />;
  }
  if (permission && !user.is_superadmin && !hasPermission(permission)) {
    return <Forbidden />;
  }

  return children;
}

function Forbidden() {
  return (
    <div className="container py-16 text-center">
      <h1 className="text-3xl font-semibold">403</h1>
      <p className="text-muted-foreground mt-2">You don't have permission to view this page</p>
    </div>
  );
}
